import { Config3D } from "../../../Config3D";
import { Event } from "../../events/Event";
import { DepthPass } from "../depthMap/DepthPass";
import { BoundFrustum } from "../math/BoundFrustum";
import { Picker } from "../utils/Picker";
import { BaseCamera } from "./BaseCamera";
import { CommandBuffer } from "./render/command/CommandBuffer";
import { RenderContext3D } from "./render/RenderContext3D";
import { Scene3D } from "./scene/Scene3D";
import { FilterMode } from "../../RenderEngine/RenderEnum/FilterMode";
import { RenderTargetFormat } from "../../RenderEngine/RenderEnum/RenderTargetFormat";
import { RenderCapable } from "../../RenderEngine/RenderEnum/RenderCapable";
import { ILaya } from "../../../ILaya";
import { TextureCube } from "../../resource/TextureCube";
import { TextureFormat } from "../../RenderEngine/RenderEnum/TextureFormat";
import { Texture2D } from "../../resource/Texture2D";
import { Matrix4x4 } from "../../maths/Matrix4x4";
import { Quaternion } from "../../maths/Quaternion";
import { Vector2 } from "../../maths/Vector2";
import { Vector3 } from "../../maths/Vector3";
import { Vector4 } from "../../maths/Vector4";
import { DepthTextureMode, RenderTexture } from "../../resource/RenderTexture";
import { Stat } from "../../utils/Stat";
import { WrapMode } from "../../RenderEngine/RenderEnum/WrapMode";
import { LayaGL } from "../../layagl/LayaGL";
import { Laya3DRender } from "../RenderObjs/Laya3DRender";
import { Transform3D } from "./Transform3D";
import { Cluster } from "../graphics/renderPath/Cluster";
import { Viewport } from "../../maths/Viewport";
export var CameraClearFlags;
(function (CameraClearFlags) {
    CameraClearFlags[CameraClearFlags["SolidColor"] = 0] = "SolidColor";
    CameraClearFlags[CameraClearFlags["Sky"] = 1] = "Sky";
    CameraClearFlags[CameraClearFlags["DepthOnly"] = 2] = "DepthOnly";
    CameraClearFlags[CameraClearFlags["Nothing"] = 3] = "Nothing";
    CameraClearFlags[CameraClearFlags["ColorOnly"] = 4] = "ColorOnly";
})(CameraClearFlags || (CameraClearFlags = {}));
export var CameraEventFlags;
(function (CameraEventFlags) {
    CameraEventFlags[CameraEventFlags["BeforeForwardOpaque"] = 0] = "BeforeForwardOpaque";
    CameraEventFlags[CameraEventFlags["BeforeSkyBox"] = 2] = "BeforeSkyBox";
    CameraEventFlags[CameraEventFlags["BeforeTransparent"] = 4] = "BeforeTransparent";
    CameraEventFlags[CameraEventFlags["BeforeImageEffect"] = 6] = "BeforeImageEffect";
    CameraEventFlags[CameraEventFlags["AfterEveryThing"] = 8] = "AfterEveryThing";
})(CameraEventFlags || (CameraEventFlags = {}));
export class Camera extends BaseCamera {
    static get _updateMark() {
        return RenderContext3D._instance._contextOBJ.cameraUpdateMask;
    }
    static set _updateMark(value) {
        RenderContext3D._instance._contextOBJ.cameraUpdateMask = value;
    }
    static drawRenderTextureByScene(camera, scene, renderTexture) {
        if (!renderTexture)
            return null;
        Scene3D._updateMark++;
        if (!scene.parent)
            scene._update();
        else {
            scene.sceneRenderableManager.renderUpdate();
            scene.skyRenderer.renderUpdate(RenderContext3D._instance);
        }
        scene._prepareSceneToRender();
        scene._setCullCamera(camera);
        let recoverTexture = camera.renderTarget;
        camera.renderTarget = renderTexture;
        let originScene = camera.scene;
        camera._scene = scene;
        camera.render(scene);
        camera.renderTarget = recoverTexture;
        scene.recaculateCullCamera();
        scene._componentDriver.callPostRender();
        camera._aftRenderMainPass();
        camera._scene = originScene;
        return renderTexture;
    }
    static getTexturePixel(texture) {
        let coverFilter = texture.filterMode;
        texture.filterMode = FilterMode.Point;
        let rtFormat = RenderTargetFormat.R8G8B8;
        let pixelData;
        let size = texture.width * texture.height;
        switch (texture.format) {
            case TextureFormat.R32G32B32A32:
            case TextureFormat.R16G16B16A16:
                rtFormat = RenderTargetFormat.R32G32B32A32;
                pixelData = new Float32Array(size * 4);
                break;
            case TextureFormat.R32G32B32:
            case TextureFormat.R16G16B16:
                rtFormat = RenderTargetFormat.R32G32B32;
                pixelData = new Float32Array(size * 3);
                break;
            case TextureFormat.R5G6B5:
            case TextureFormat.R8G8B8:
                rtFormat = RenderTargetFormat.R8G8B8;
                pixelData = new Uint8Array(size * 3);
                break;
            default:
                rtFormat = RenderTargetFormat.R8G8B8A8;
                pixelData = new Uint8Array(size * 4);
                break;
        }
        let rt = RenderTexture.createFromPool(texture.width, texture.height, rtFormat, RenderTargetFormat.None, false, 0, false);
        let cmd = new CommandBuffer();
        cmd.blitScreenQuad(texture, rt);
        cmd.context = RenderContext3D._instance;
        cmd._applyOne();
        texture.filterMode = coverFilter;
        rt.getData(0, 0, texture.width, texture.height, pixelData);
        rt.destroy();
        return pixelData;
    }
    static getTexturePixelAsync(texture) {
        let coverFilter = texture.filterMode;
        texture.filterMode = FilterMode.Point;
        let rtFormat = RenderTargetFormat.R8G8B8;
        let pixelData;
        let size = texture.width * texture.height;
        switch (texture.format) {
            case TextureFormat.R32G32B32A32:
            case TextureFormat.R16G16B16A16:
                rtFormat = RenderTargetFormat.R32G32B32A32;
                pixelData = new Float32Array(size * 4);
                break;
            case TextureFormat.R32G32B32:
            case TextureFormat.R16G16B16:
                rtFormat = RenderTargetFormat.R32G32B32;
                pixelData = new Float32Array(size * 3);
                break;
            case TextureFormat.R5G6B5:
            case TextureFormat.R8G8B8:
                rtFormat = RenderTargetFormat.R8G8B8;
                pixelData = new Uint8Array(size * 3);
                break;
            default:
                rtFormat = RenderTargetFormat.R8G8B8A8;
                pixelData = new Uint8Array(size * 4);
                break;
        }
        let rt = RenderTexture.createFromPool(texture.width, texture.height, rtFormat, RenderTargetFormat.None, false, 0, false);
        let cmd = new CommandBuffer();
        cmd.blitScreenQuad(texture, rt);
        cmd.context = RenderContext3D._instance;
        cmd._applyOne();
        texture.filterMode = coverFilter;
        const pd = rt.getDataAsync(0, 0, texture.width, texture.height, pixelData);
        rt.destroy();
        return pd;
    }
    static drawTextureCubePixelByScene(camera, scene, renderCubeSize, format, cullingMask) {
        let rtFormat = RenderTargetFormat.R8G8B8;
        let pixelData;
        let size = renderCubeSize * renderCubeSize;
        let bytelength;
        switch (format) {
            case TextureFormat.R32G32B32A32:
            case TextureFormat.R16G16B16A16:
                rtFormat = RenderTargetFormat.R32G32B32A32;
                size *= 4;
                bytelength = 4;
                break;
            case TextureFormat.R32G32B32:
            case TextureFormat.R16G16B16:
                rtFormat = RenderTargetFormat.R32G32B32;
                size *= 3;
                bytelength = 4;
                break;
            case TextureFormat.R5G6B5:
            case TextureFormat.R8G8B8:
                rtFormat = RenderTargetFormat.R8G8B8;
                size *= 3;
                bytelength = 1;
                break;
            case TextureFormat.R8G8B8A8:
                rtFormat = RenderTargetFormat.R8G8B8A8;
                pixelData = new Uint8Array(size * 4);
                size *= 4;
                bytelength = 1;
                break;
            default:
                throw new Error("Type is not supported");
        }
        let rt = new RenderTexture(renderCubeSize, renderCubeSize, rtFormat, RenderTargetFormat.DEPTH_16, false, 0, false, false);
        camera.fieldOfView = 90;
        camera.cullingMask = cullingMask;
        let pixels = [];
        let quaterionArray = [
            new Quaternion(0, 1, 0, 0),
            new Quaternion(0, 0, 0, 1),
            new Quaternion(0, 0.7071068, 0, 0.7071068),
            new Quaternion(0, 0.7071068, 0, -0.7071068),
            new Quaternion(0, 0.7071068, -0.7071068, 0),
            new Quaternion(0, -0.7071068, -0.7071068, 0),
        ];
        for (var i = 0; i < 6; i++) {
            camera.transform.rotation = quaterionArray[i];
            this.drawRenderTextureByScene(camera, scene, rt);
            if (bytelength == 4)
                pixelData = new Float32Array(size);
            else
                pixelData = new Uint8Array(size);
            pixels[i] = rt.getData(0, 0, renderCubeSize, renderCubeSize, pixelData);
        }
        rt.destroy();
        return pixels;
    }
    static drawTextureCubeByScene(camera, position, scene, renderCubeSize, format, cullingMask = 0) {
        camera.transform.position = position;
        let pixels = this.drawTextureCubePixelByScene(camera, scene, renderCubeSize, format, cullingMask);
        let finalformat;
        switch (format) {
            case TextureFormat.R32G32B32A32:
            case TextureFormat.R16G16B16A16:
                finalformat = TextureFormat.R32G32B32A32;
                break;
            case TextureFormat.R32G32B32:
            case TextureFormat.R16G16B16:
                finalformat = TextureFormat.R32G32B32;
                break;
            case TextureFormat.R5G6B5:
            case TextureFormat.R8G8B8:
                finalformat = TextureFormat.R8G8B8;
                break;
            case TextureFormat.R8G8B8A8:
                finalformat = TextureFormat.R8G8B8A8;
                break;
            default:
                throw new Error("Type is not supported");
        }
        let textureCube = new TextureCube(renderCubeSize, format, true, false);
        textureCube.setPixelsData(pixels, false, false);
        return textureCube;
    }
    static __init__() {
        Camera.depthPass = new DepthPass();
    }
    set nearPlane(value) {
        super.nearPlane = value;
        this._renderDataModule.nearplane = value;
    }
    get nearPlane() {
        return this._nearPlane;
    }
    set farPlane(value) {
        super.farPlane = value;
        this._renderDataModule.farplane = value;
    }
    get farPlane() {
        return this._farPlane;
    }
    set fieldOfView(value) {
        super.fieldOfView = value;
        this._renderDataModule.fieldOfView = value;
    }
    get fieldOfView() {
        return this._fieldOfView;
    }
    get aspectRatio() {
        if (this._aspectRatio === 0) {
            var vp = this.viewport;
            return vp.width / vp.height;
        }
        return this._aspectRatio;
    }
    set aspectRatio(value) {
        if (value < 0)
            throw new Error("Camera: the aspect ratio has to be a positive real number.");
        this._aspectRatio = value;
        this._renderDataModule.aspectRatio = value;
        this._calculateProjectionMatrix();
    }
    get viewport() {
        if (this._offScreenRenderTexture)
            this._calculationViewport(this._normalizedViewport, this._offScreenRenderTexture.width, this._offScreenRenderTexture.height);
        else
            this._calculationViewport(this._normalizedViewport, this.clientWidth, this.clientHeight);
        return this._viewport;
    }
    set viewport(value) {
        var width;
        var height;
        if (this._offScreenRenderTexture) {
            width = this._offScreenRenderTexture.width;
            height = this._offScreenRenderTexture.height;
        }
        else {
            width = this.clientWidth;
            height = this.clientHeight;
        }
        this._normalizedViewport.x = value.x / width;
        this._normalizedViewport.y = value.y / height;
        this._normalizedViewport.width = value.width / width;
        this._normalizedViewport.height = value.height / height;
        this._calculationViewport(this._normalizedViewport, width, height);
        this._calculateProjectionMatrix();
    }
    get clientWidth() {
        ILaya.stage.needUpdateCanvasSize();
        if (Config3D.customResolution)
            return Config3D.pixelRatio * Config3D._resoluWidth | 0;
        else
            return RenderContext3D.clientWidth * Config3D.pixelRatio | 0;
    }
    get clientHeight() {
        ILaya.stage.needUpdateCanvasSize();
        if (Config3D.customResolution)
            return Config3D.pixelRatio * Config3D._resoluHeight | 0;
        else
            return RenderContext3D.clientHeight * Config3D.pixelRatio | 0;
    }
    set msaa(value) {
        LayaGL.renderEngine.getCapable(RenderCapable.MSAA) ? this._msaa = value : this._msaa = false;
    }
    get msaa() {
        return this._msaa && Stat.enablemsaa;
    }
    set fxaa(value) {
        this._fxaa = value;
    }
    get fxaa() {
        return this._fxaa;
    }
    get normalizedViewport() {
        return this._normalizedViewport;
    }
    set normalizedViewport(value) {
        var width;
        var height;
        if (this._offScreenRenderTexture) {
            width = this._offScreenRenderTexture.width;
            height = this._offScreenRenderTexture.height;
        }
        else {
            width = this.clientWidth;
            height = this.clientHeight;
        }
        if (this._normalizedViewport !== value)
            value.cloneTo(this._normalizedViewport);
        this._calculationViewport(value, width, height);
        this._calculateProjectionMatrix();
    }
    get viewMatrix() {
        if (this._updateViewMatrix) {
            var scale = this.transform.getWorldLossyScale();
            var scaleX = scale.x;
            var scaleY = scale.y;
            var scaleZ = scale.z;
            var viewMatE = this._viewMatrix.elements;
            this.transform.worldMatrix.cloneTo(this._viewMatrix);
            viewMatE[0] /= scaleX;
            viewMatE[1] /= scaleX;
            viewMatE[2] /= scaleX;
            viewMatE[4] /= scaleY;
            viewMatE[5] /= scaleY;
            viewMatE[6] /= scaleY;
            viewMatE[8] /= scaleZ;
            viewMatE[9] /= scaleZ;
            viewMatE[10] /= scaleZ;
            this._viewMatrix.invert(this._viewMatrix);
            this._updateViewMatrix = false;
            if (this.skyRenderElement._renderElementOBJ)
                this.skyRenderElement.calculateViewMatrix(this._viewMatrix);
            this._updateViewProjectionMatrix = true;
        }
        return this._viewMatrix;
    }
    get projectionMatrix() {
        return this._projectionMatrix;
    }
    set projectionMatrix(value) {
        this._projectionMatrix = value;
        this._useUserProjectionMatrix = true;
    }
    get projectionViewMatrix() {
        this.updateViewProjectionMatrix();
        return this._projectionViewMatrix;
    }
    updateViewProjectionMatrix() {
        let viewMatrix = this.viewMatrix;
        let projectionMatrix = this.projectionMatrix;
        if (this._updateViewProjectionMatrix) {
            Matrix4x4.multiply(projectionMatrix, viewMatrix, this._projectionViewMatrix);
            this._updateViewProjectionMatrix = false;
            this._boundFrustum.matrix = this._projectionViewMatrix;
        }
        this._renderDataModule.setProjectionViewMatrix(this._projectionViewMatrix);
    }
    get boundFrustum() {
        this.updateViewProjectionMatrix();
        return this._boundFrustum;
    }
    get renderTarget() {
        return this._offScreenRenderTexture;
    }
    set renderTarget(value) {
        var lastValue = this._offScreenRenderTexture;
        if (lastValue !== value) {
            (lastValue) && (lastValue._isCameraTarget = false);
            (value) && (value._isCameraTarget = true);
            this._offScreenRenderTexture = value;
            this._calculateProjectionMatrix();
        }
    }
    get postProcess() {
        return this._postProcess;
    }
    set postProcess(value) {
        this._postProcess = value;
    }
    get enableHDR() {
        return this._enableHDR;
    }
    set enableHDR(value) {
        if (value && !LayaGL.renderEngine.getCapable(RenderCapable.RenderTextureFormat_R16G16B16A16)) {
            console.warn("Camera:can't enable HDR in this device.");
            return;
        }
        this._enableHDR = value;
    }
    get enableBuiltInRenderTexture() {
        return this._needBuiltInRenderTexture;
    }
    set enableBuiltInRenderTexture(value) {
        this._needBuiltInRenderTexture = value;
    }
    get depthTextureMode() {
        return this._depthTextureMode;
    }
    set depthTextureMode(value) {
        this._depthTextureMode = value;
        if (!LayaGL.renderEngine.getCapable(RenderCapable.RenderTextureFormat_Depth)) {
            this._depthTextureMode &= ~DepthTextureMode.Depth;
        }
    }
    set opaquePass(value) {
        if (value == this._opaquePass)
            return;
        if (!value) {
            this._shaderValues.setTexture(BaseCamera.OPAQUETEXTURE, Texture2D.blackTexture);
            this._shaderValues.setVector(BaseCamera.OPAQUETEXTUREPARAMS, Vector4.ONE);
            this._opaqueTexture && RenderTexture.recoverToPool(this._opaqueTexture);
            this._opaqueTexture = null;
        }
        this._opaquePass = value;
    }
    get opaquePass() {
        return this._opaquePass;
    }
    get depthTextureFormat() {
        return this._depthTextureFormat;
    }
    set depthTextureFormat(value) {
        this._depthTextureFormat = value;
    }
    set enableBlitDepth(value) {
        if (value == this._canBlitDepth)
            return;
        this._canBlitDepth = value;
        this._cacheDepth = value;
        this._internalRenderTexture && RenderTexture.recoverToPool(this._internalRenderTexture);
        this._internalRenderTexture = RenderTexture.createFromPool(this.viewport.width, this.viewport.height, this._getRenderTextureFormat(), this.depthTextureFormat, false, this.msaa ? 4 : 1, this._canBlitDepth, this._needRenderGamma(this._getRenderTextureFormat()));
        if (!value) {
            if (this._cacheDepthTexture)
                this._cacheDepthTexture._inPool ? 0 : RenderTexture.recoverToPool(this._cacheDepthTexture);
        }
    }
    get enableBlitDepth() {
        return this._canBlitDepth;
    }
    get canblitDepth() {
        return this._canBlitDepth && this._internalRenderTexture && this._internalRenderTexture.depthStencilFormat != null;
    }
    constructor(aspectRatio = 0, nearPlane = 0.3, farPlane = 1000) {
        super(nearPlane, farPlane);
        this._updateViewMatrix = true;
        this._updateViewProjectionMatrix = true;
        this._postProcess = null;
        this._enableHDR = false;
        this._viewportParams = new Vector4();
        this._projectionParams = new Vector4();
        this._needBuiltInRenderTexture = false;
        this._msaa = false;
        this._fxaa = false;
        this._offScreenRenderTexture = null;
        this._internalRenderTexture = null;
        this._canBlitDepth = false;
        this._internalCommandBuffer = new CommandBuffer();
        this._depthTextureFormat = RenderTargetFormat.DEPTH_32;
        this._cameraEventCommandBuffer = {};
        this._shadowCasterCommanBuffer = [];
        this._clusterPlaneCacheFlag = new Vector2(-1, -1);
        this._screenOffsetScale = new Vector4();
        this.enableRender = true;
        this.clearFlag = CameraClearFlags.SolidColor;
        this.opaqueTextureSize = 512;
        this._renderDataModule = Laya3DRender.Render3DModuleDataFactory.createCameraModuleData();
        this._Render3DProcess = Laya3DRender.Render3DPassFactory.createRender3DProcess();
        this._renderDataModule.transform = this.transform;
        this._viewMatrix = new Matrix4x4();
        this._projectionMatrix = new Matrix4x4();
        this._projectionViewMatrix = new Matrix4x4();
        this._viewport = new Viewport(0, 0, 0, 0);
        this._normalizedViewport = new Viewport(0, 0, 1, 1);
        this._rayViewport = new Viewport(0, 0, 0, 0);
        this._aspectRatio = aspectRatio;
        this._boundFrustum = new BoundFrustum(new Matrix4x4());
        this.depthTextureMode = 0;
        this.opaquePass = false;
        this._calculateProjectionMatrix();
        ILaya.stage.on(Event.RESIZE, this, this._onScreenSizeChanged);
        this.transform.on(Event.TRANSFORM_CHANGED, this, this._onTransformChanged);
        this.opaquePass = false;
        this._internalCommandBuffer.context = RenderContext3D._instance;
        this._renderDataModule.farplane = this.farPlane;
        this._renderDataModule.nearplane = this.nearPlane;
        this._renderDataModule.fieldOfView = this.fieldOfView;
        this._renderDataModule.aspectRatio = this.aspectRatio;
    }
    _calculationViewport(normalizedViewport, width, height) {
        var lx = normalizedViewport.x * width;
        var ly = normalizedViewport.y * height;
        var rx = lx + Math.max(normalizedViewport.width * width, 0);
        var ry = ly + Math.max(normalizedViewport.height * height, 0);
        var ceilLeftX = Math.ceil(lx);
        var ceilLeftY = Math.ceil(ly);
        var floorRightX = Math.floor(rx);
        var floorRightY = Math.floor(ry);
        var pixelLeftX = ceilLeftX - lx >= 0.5 ? Math.floor(lx) : ceilLeftX;
        var pixelLeftY = ceilLeftY - ly >= 0.5 ? Math.floor(ly) : ceilLeftY;
        var pixelRightX = rx - floorRightX >= 0.5 ? Math.ceil(rx) : floorRightX;
        var pixelRightY = ry - floorRightY >= 0.5 ? Math.ceil(ry) : floorRightY;
        this._viewport.x = pixelLeftX;
        this._viewport.y = pixelLeftY;
        this._viewport.width = pixelRightX - pixelLeftX;
        this._viewport.height = pixelRightY - pixelLeftY;
    }
    _calculateProjectionMatrix() {
        if (!this._useUserProjectionMatrix) {
            if (this._orthographic) {
                var halfHeight = this.orthographicVerticalSize * 0.5;
                var halfWidth = halfHeight * this.aspectRatio;
                Matrix4x4.createOrthoOffCenter(-halfWidth, halfWidth, -halfHeight, halfHeight, this.nearPlane, this.farPlane, this._projectionMatrix);
            }
            else {
                Matrix4x4.createPerspective(3.1416 * this.fieldOfView / 180.0, this.aspectRatio, this.nearPlane, this.farPlane, this._projectionMatrix);
            }
            if (this.skyRenderElement._renderElementOBJ)
                this.skyRenderElement.caluclateProjectionMatrix(this._projectionMatrix, this.aspectRatio, this.nearPlane, this.farPlane, this.fieldOfView, this.orthographic);
            this._updateViewProjectionMatrix = true;
        }
    }
    _isLayerVisible(layer) {
        return (Math.pow(2, layer) & this.cullingMask) != 0;
    }
    _onTransformChanged(flag) {
        flag &= Transform3D.TRANSFORM_WORLDMATRIX;
        (flag) && (this._updateViewMatrix = true);
    }
    clone() {
        let camera = super.clone();
        camera.clearFlag = this.clearFlag;
        this.clearColor.cloneTo(camera.clearColor);
        camera.clearColor = camera.clearColor;
        camera.viewport = this.viewport;
        this.normalizedViewport.cloneTo(camera.normalizedViewport);
        camera.enableHDR = this.enableHDR;
        camera.farPlane = this.farPlane;
        camera.nearPlane = this.nearPlane;
        camera.fieldOfView = this.fieldOfView;
        camera.orthographic = this.orthographic;
        camera.orthographicVerticalSize = this.orthographicVerticalSize;
        camera.opaquePass = this.opaquePass;
        camera._cameraEventCommandBuffer = this._cameraEventCommandBuffer;
        camera.opaquePass = this.opaquePass;
        return camera;
    }
    _getCanvasWidth() {
        if (this._offScreenRenderTexture)
            return this._offScreenRenderTexture.width;
        else
            return this.clientWidth;
    }
    _getCanvasHeight() {
        if (this._offScreenRenderTexture)
            return this._offScreenRenderTexture.height;
        else
            return this.clientHeight;
    }
    _getRenderTexture() {
        return this._internalRenderTexture || this._offScreenRenderTexture;
    }
    _needRenderGamma(rt) {
        switch (rt) {
            case RenderTargetFormat.R8G8B8:
            case RenderTargetFormat.R8G8B8A8:
                return true;
            default:
                return false;
        }
    }
    _needInternalRenderTexture() {
        let needInternalRT = this.enableBuiltInRenderTexture;
        if (this.renderTarget) {
            if (this.msaa) {
                needInternalRT = needInternalRT || !(this.renderTarget.samples > 1);
            }
            if (this.enableHDR) {
                switch (this.renderTarget.format) {
                    case TextureFormat.R16G16B16A16:
                    case TextureFormat.R16G16B16:
                    case TextureFormat.R32G32B32A32:
                    case TextureFormat.R32G32B32:
                        break;
                    default:
                        needInternalRT = true;
                        break;
                }
            }
            if (this.postProcess && this.postProcess.enable && this.postProcess.effects.length > 0) {
                needInternalRT = true;
            }
            if (this.normalizedViewport.width != 1 || this.normalizedViewport.height != 1 || this.normalizedViewport.x != 0 || this.normalizedViewport.y != 0) {
                needInternalRT = true;
            }
        }
        return needInternalRT;
    }
    _getRenderTextureFormat() {
        if (this._enableHDR)
            return RenderTargetFormat.R16G16B16A16;
        else
            return RenderTargetFormat.R8G8B8A8;
    }
    _updateCameraRenderData(context) {
        this._prepareCameraToRender();
        this._applyViewProject(this.viewMatrix, this._projectionMatrix, context.invertY);
        this._contextApply(context);
    }
    _prepareCameraToRender() {
        super._prepareCameraToRender();
        var vp = this.viewport;
        this._viewportParams.setValue(vp.x, vp.y, vp.width, vp.height);
        let invertY = RenderContext3D._instance.invertY;
        this._projectionParams.setValue(this._nearPlane, this._farPlane, invertY ? -1 : 1, 1 / this.farPlane);
        this._shaderValues.setVector(BaseCamera.VIEWPORT, this._viewportParams);
        this._shaderValues.setVector(BaseCamera.PROJECTION_PARAMS, this._projectionParams);
    }
    _contextApply(context) {
        context.viewMatrix = this.viewMatrix;
        context.projectionMatrix = this.projectionMatrix;
        context.projectionViewMatrix = this.projectionViewMatrix;
    }
    _applyViewProject(viewMat, proMat, invertY) {
        var projectView;
        if (invertY) {
            Matrix4x4.multiply(BaseCamera._invertYScaleMatrix, proMat, BaseCamera._invertYProjectionMatrix);
            Matrix4x4.multiply(BaseCamera._invertYProjectionMatrix, viewMat, BaseCamera._invertYProjectionViewMatrix);
            proMat = BaseCamera._invertYProjectionMatrix;
            projectView = BaseCamera._invertYProjectionViewMatrix;
        }
        else {
            Matrix4x4.multiply(proMat, viewMat, this._projectionViewMatrix);
            this._renderDataModule.setProjectionViewMatrix(this._projectionViewMatrix);
            projectView = this._projectionViewMatrix;
        }
        this._shaderValues.setMatrix4x4(BaseCamera.VIEWMATRIX, viewMat);
        this._shaderValues.setMatrix4x4(BaseCamera.PROJECTMATRIX, proMat);
        this._shaderValues.setMatrix4x4(BaseCamera.VIEWPROJECTMATRIX, projectView);
    }
    _updateClusterPlaneXY() {
        var fieldOfView = this.fieldOfView;
        var aspectRatio = this.aspectRatio;
        if (this._clusterPlaneCacheFlag.x !== fieldOfView || this._clusterPlaneCacheFlag.y !== aspectRatio) {
            var clusterCount = Config3D.lightClusterCount;
            var xSlixe = clusterCount.x, ySlice = clusterCount.y;
            var xCount = xSlixe + 1, yCount = ySlice + 1;
            var xPlanes = this._clusterXPlanes, yPlanes = this._clusterYPlanes;
            if (!xPlanes) {
                xPlanes = this._clusterXPlanes = new Array(xCount);
                yPlanes = this._clusterYPlanes = new Array(yCount);
                for (var i = 0; i < xCount; i++)
                    xPlanes[i] = new Vector3();
                for (var i = 0; i < yCount; i++)
                    yPlanes[i] = new Vector3();
            }
            var halfY = Math.tan((this.fieldOfView / 2) * Math.PI / 180);
            var halfX = this.aspectRatio * halfY;
            var yLengthPerCluster = 2 * halfY / ySlice;
            var xLengthPerCluster = 2 * halfX / xSlixe;
            for (var i = 0; i < xCount; i++) {
                var angle = -halfX + xLengthPerCluster * i;
                var bigHypot = Math.sqrt(1 + angle * angle);
                var normX = 1 / bigHypot;
                var xPlane = xPlanes[i];
                xPlane.setValue(normX, 0, -angle * normX);
            }
            for (var i = 0; i < yCount; i++) {
                var angle = halfY - yLengthPerCluster * i;
                var bigHypot = Math.sqrt(1 + angle * angle);
                var normY = -1 / bigHypot;
                var yPlane = yPlanes[i];
                yPlane.setValue(0, normY, -angle * normY);
            }
            this._clusterPlaneCacheFlag.x = fieldOfView;
            this._clusterPlaneCacheFlag.y = aspectRatio;
        }
    }
    _addCasterShadowCommandBuffer(commandBuffer) {
        if (this._shadowCasterCommanBuffer.indexOf(commandBuffer) < 0)
            this._shadowCasterCommanBuffer.push(commandBuffer);
    }
    _removeCasterShadowCommandBuffer(commandBuffer) {
        var index = this._shadowCasterCommanBuffer.indexOf(commandBuffer);
        if (index != -1)
            this._shadowCasterCommanBuffer.splice(index, 1);
    }
    _preRenderMainPass(context, scene, needInternalRT, viewport) {
        context.camera = this;
        context.cameraShaderValue = this._shaderValues;
        Camera._updateMark++;
        if (needInternalRT && !this._offScreenRenderTexture && (this.clearFlag == CameraClearFlags.DepthOnly || this.clearFlag == CameraClearFlags.Nothing)) {
            if (RenderTexture.bindCanvasRender) {
                if (RenderTexture.bindCanvasRender != this._internalRenderTexture) {
                    this._internalCommandBuffer.clear();
                    this._internalCommandBuffer.blitScreenQuad(RenderTexture.bindCanvasRender, this._internalRenderTexture);
                    this._internalCommandBuffer._applyOne();
                }
            }
            else {
                if (this._enableHDR) {
                    var grabTexture = RenderTexture.createFromPool(viewport.width, viewport.height, RenderTargetFormat.R8G8B8, RenderTargetFormat.DEPTH_16, false, 1);
                    grabTexture.filterMode = FilterMode.Bilinear;
                    this._renderEngine.copySubFrameBuffertoTex(grabTexture._texture, 0, 0, 0, viewport.x, RenderContext3D.clientHeight - (viewport.y + viewport.height), viewport.width, viewport.height);
                    this._internalCommandBuffer.clear();
                    this._internalCommandBuffer.blitScreenQuad(grabTexture, this._internalRenderTexture);
                    this._internalCommandBuffer._apply(true);
                    RenderTexture.recoverToPool(grabTexture);
                }
            }
        }
    }
    get depthTexture() {
        return this._depthTexture;
    }
    set depthTexture(value) {
        this._depthTexture = value;
    }
    get depthNormalTexture() {
        return this._depthNormalsTexture;
    }
    set depthNormalTexture(value) {
        this._depthNormalsTexture = value;
    }
    _aftRenderMainPass() {
        if (this._cacheDepth && this._internalRenderTexture) {
            if (this._cacheDepthTexture)
                this._cacheDepthTexture._inPool ? 0 : RenderTexture.recoverToPool(this._cacheDepthTexture);
            this._cacheDepthTexture = this._internalRenderTexture;
        }
        else {
            this._internalRenderTexture && RenderTexture.recoverToPool(this._internalRenderTexture);
        }
    }
    _createOpaqueTexture() {
        if (!this._opaqueTexture) {
            let tex = this._getRenderTexture();
            let size = this.opaqueTextureSize;
            this._opaqueTexture = RenderTexture.createFromPool(size, size, tex.colorFormat, RenderTargetFormat.None, true, 1, false, true);
            this._opaqueTexture.filterMode = FilterMode.Bilinear;
            this._opaqueTexture.wrapModeU = WrapMode.Clamp;
            this._opaqueTexture.wrapModeV = WrapMode.Clamp;
            this._shaderValues.setTexture(BaseCamera.OPAQUETEXTURE, this._opaqueTexture);
            let opaqueTexParams = new Vector4();
            opaqueTexParams.x = this._opaqueTexture.width;
            opaqueTexParams.y = this._opaqueTexture.height;
            opaqueTexParams.z = this._opaqueTexture.maxMipmapLevel;
            this._shaderValues.setVector(BaseCamera.OPAQUETEXTUREPARAMS, opaqueTexParams);
        }
    }
    render(scene) {
        let context = RenderContext3D._instance;
        context.scene = scene;
        context.camera = this;
        scene._setCullCamera(this);
        this._Render3DProcess.render3DManager = scene.sceneRenderableManager._sceneManagerOBJ;
        let viewport = this.viewport;
        let needInternalRT = this._needInternalRenderTexture();
        if (needInternalRT) {
            let multiSampler = this.msaa ? 4 : 1;
            let frameFormat = this._getRenderTextureFormat();
            let depthFormat = this.depthTextureFormat;
            let gamma = this._needRenderGamma(frameFormat);
            let internalRT = RenderTexture.createFromPool(viewport.width, viewport.height, frameFormat, depthFormat, false, multiSampler, this.canblitDepth, gamma);
            internalRT.filterMode = FilterMode.Bilinear;
            this._internalRenderTexture = internalRT;
        }
        else {
            this._internalRenderTexture = null;
        }
        if (this.opaquePass && !this._opaqueTexture) {
            this._createOpaqueTexture();
        }
        context.invertY = false;
        let renderRT = this._getRenderTexture();
        if (renderRT) {
            context.invertY = renderRT._isCameraTarget;
        }
        this._prepareCameraToRender();
        this._applyViewProject(this.viewMatrix, this.projectionMatrix, context.invertY);
        this._contextApply(context);
        if (this.clearFlag == CameraClearFlags.Sky) {
            scene.skyRenderer.setRenderElement(this.skyRenderElement);
            this.skyRenderElement.renderpre(context);
        }
        scene._componentDriver.callPreRender();
        this._preRenderMainPass(context, scene, needInternalRT, viewport);
        let multiLight = Config3D._multiLighting;
        if (multiLight) {
            Cluster.instance.update(this, scene);
        }
        this._Render3DProcess.fowardRender(context._contextOBJ, this);
        scene._componentDriver.callPostRender();
    }
    viewportPointToRay(point, out) {
        _tempVector20.setValue(point.x * ILaya.stage.clientScaleX * Config3D.pixelRatio, point.y * ILaya.stage.clientScaleY * Config3D.pixelRatio);
        this._rayViewport.x = this.viewport.x;
        this._rayViewport.y = this.viewport.y;
        this._rayViewport.width = this.viewport.width;
        this._rayViewport.height = this.viewport.height;
        Picker.calculateCursorRay(_tempVector20, this._rayViewport, this._projectionMatrix, this.viewMatrix, null, out);
    }
    normalizedViewportPointToRay(point, out) {
        var vp = this.normalizedViewport;
        _tempVector20.x = point.x * Config3D.pixelRatio * vp.width;
        _tempVector20.y = point.y * Config3D.pixelRatio * vp.height;
        Picker.calculateCursorRay(_tempVector20, this.viewport, this._projectionMatrix, this.viewMatrix, null, out);
    }
    worldToViewportPoint(position, out) {
        Matrix4x4.multiply(this._projectionMatrix, this._viewMatrix, this._projectionViewMatrix);
        this.viewport.project(position, this._projectionViewMatrix, out);
        var r = Config3D.pixelRatio;
        let _wr = (out.x - this.viewport.x) / r;
        let _hr = (out.y - this.viewport.y) / r;
        out.x = _wr + this.viewport.x;
        out.y = _hr + this.viewport.y;
        out.x = (out.x / ILaya.stage.clientScaleX) | 0;
        out.y = (out.y / ILaya.stage.clientScaleY) | 0;
    }
    worldToNormalizedViewportPoint(position, out) {
        this.worldToViewportPoint(position, out);
        out.x = out.x / ILaya.stage.width;
        out.y = out.y / ILaya.stage.height;
    }
    convertScreenCoordToOrthographicCoord(source, out) {
        if (this._orthographic) {
            var clientWidth = this.clientWidth;
            var clientHeight = this.clientHeight;
            var ratioX = this.orthographicVerticalSize * this.aspectRatio / clientWidth;
            var ratioY = this.orthographicVerticalSize / clientHeight;
            out.x = (-clientWidth / 2 + source.x * ILaya.stage.clientScaleX) * ratioX;
            out.y = (clientHeight / 2 - source.y * ILaya.stage.clientScaleY) * ratioY;
            out.z = (this.nearPlane - this.farPlane) * (source.z + 1) / 2 - this.nearPlane;
            Vector3.transformCoordinate(out, this.transform.worldMatrix, out);
            return true;
        }
        else {
            return false;
        }
    }
    destroy(destroyChild = true) {
        this._shaderValues.destroy();
        this._internalRenderTexture && (!this._internalRenderTexture._inPool) && RenderTexture.recoverToPool(this._internalRenderTexture);
        this._offScreenRenderTexture = null;
        if (this._opaqueTexture) {
            RenderTexture.recoverToPool(this._opaqueTexture);
        }
        this._Render3DProcess.destroy();
        this.transform.off(Event.TRANSFORM_CHANGED, this, this._onTransformChanged);
        ILaya.stage.off(Event.RESIZE, this, this._onScreenSizeChanged);
        this._cameraEventCommandBuffer = {};
        if (RenderContext3D._instance.camera == this) {
            RenderContext3D._instance.cameraShaderValue = null;
            RenderContext3D._instance.camera = null;
        }
        super.destroy(destroyChild);
    }
    addCommandBuffer(event, commandBuffer) {
        var commandBufferArray = this._cameraEventCommandBuffer[event];
        if (!commandBufferArray)
            commandBufferArray = this._cameraEventCommandBuffer[event] = [];
        if (commandBufferArray.indexOf(commandBuffer) < 0)
            commandBufferArray.push(commandBuffer);
        commandBuffer._camera = this;
        if (commandBuffer.casterShadow) {
            this._addCasterShadowCommandBuffer(commandBuffer);
        }
    }
    removeCommandBuffer(event, commandBuffer) {
        var commandBufferArray = this._cameraEventCommandBuffer[event];
        if (commandBufferArray) {
            var index = commandBufferArray.indexOf(commandBuffer);
            if (index != -1)
                commandBufferArray.splice(index, 1);
            commandBuffer.casterShadow && this._removeCasterShadowCommandBuffer(commandBuffer);
        }
        else
            throw "Camera:unknown event.";
    }
    removeCommandBuffers(event) {
        if (this._cameraEventCommandBuffer[event])
            this._cameraEventCommandBuffer[event].length = 0;
    }
}
Camera._context3DViewPortCatch = new Viewport(0, 0, 0, 0);
Camera._contextScissorPortCatch = new Vector4(0, 0, 0, 0);
const _tempVector20 = new Vector2();
